import { errorMessage } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useToasts } from "react-toast-notifications";
import transportService from "services/transportService";
import { formConvertErrorMessage } from "../../helpers/form_helper";

export const useListTransport = (payload, onSuccess, onError) => {
  const { params } = payload;
  return useQuery(
    ["transport-list"],
    () => transportService.getTransports(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object({ data: [] }),
      select: response => {
        const { data } = response;
        return {
          items: data?.items || [],
          params: params,
          totalCount: data?._meta
            ? data?._meta?.totalCount
            : data?.items?.length,
        };
      },
    }
  );
};

export const useTransports = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["common"]);
  return useQuery(
    ["transports", "all", payload.params.key],
    () => transportService.getAllTransports(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_transport", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object({ data: {} }),
      select: response => {
        return response;
      },
    }
  );
};

// Detail transport
export const useDetailTransport = (payload, onSuccess, onError) => {
  return useQuery(
    ["transport", "detail", payload],
    () => transportService.detailTransport(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

// Create Transport
export const useCreateTransport = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["transport"]);
  const { addToast } = useToasts();

  return useMutation(transportService.createTransport, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.create", {
          target: t("transport"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.create", {
              target: t("transport"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Update Transport
export const useUpdateTransport = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["transport"]);
  const { addToast } = useToasts();

  return useMutation(transportService.updateTransport, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.update", {
          target: t("transport"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.update", {
              target: t("transport"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Delete Transport
export const useDeleteTransport = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["transport"]);
  const { addToast } = useToasts();

  return useMutation(transportService.deleteTransport, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.delete", {
          target: t("transport"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.delete", {
              target: t("transport"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Retry Order
export const useRetryOrder = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["transport"]);
  const { addToast } = useToasts();

  return useMutation(transportService.retryOrder, {
    retry: 0,
    onSuccess: res => {
      if (res?.data?.result) {
        addToast(
          t("message.retry_order", {
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
      } else {
        addToast(
          t("message.retry_order", {
            status: t("message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (res?.data?.message.length > 0) {
          addToast(res?.data?.message.join("\n"), {
            appearance: "error",
            autoDismiss: true,
          });
        }
      }
      if (typeof onSuccess === "function") {
        onSuccess(res?.data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.retry_order", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useListShippingType = (payload, onSuccess, onError) => {
  return useQuery(
    ["list-shipping-type"],
    () => transportService.getListShippingType(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object({ data: [] }),
      select: response => response?.data,
    }
  );
};
